import type { Experiment } from '@/types'

// --- Helpers ---

function daysBetween(dateStr: string, now: Date): number {
  const d = new Date(dateStr)
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24)))
}

// --- Per-experiment monthly impact projection ---

export interface ExperimentImpactProjection {
  projectedMonthlyGain: number
  liftAbsolute: number
  bestVariant: string
  treatmentAOV: number
  experimentMonthlySessions: number
  usedStoreAOVFallback: boolean
}

/**
 * Project monthly revenue impact for a single active experiment.
 * Uses the experiment's own session rate (not store-wide traffic) and the
 * best-performing non-control variant's AOV. Falls back to store-level AOV
 * when the variant has no orders yet.
 */
export function projectExperimentMonthlyGain(
  results: any,
  storeAOV: number = 0
): ExperimentImpactProjection | null {
  if (!results?.summary) return null

  const lift = results.summary.lift
  if (!lift || !(lift.absolute > 0)) return null

  const variants = (results.variants || {}) as Record<string, any>
  const nonControlKeys = Object.keys(variants).filter(k => k !== 'control')
  if (nonControlKeys.length === 0) return null

  let bestVariant = nonControlKeys[0]
  let bestRate = variants[bestVariant]?.conversion_rate ?? 0
  for (const k of nonControlKeys) {
    const r = variants[k]?.conversion_rate ?? 0
    if (r > bestRate) {
      bestRate = r
      bestVariant = k
    }
  }

  let treatmentAOV = variants[bestVariant]?.average_order_value ?? 0
  let usedStoreAOVFallback = false
  if (treatmentAOV <= 0) {
    if (storeAOV > 0) {
      treatmentAOV = storeAOV
      usedStoreAOVFallback = true
    } else {
      return null
    }
  }

  const expSessions = results.summary?.total_sessions ??
    Object.values((results.sessions || {}) as Record<string, number>).reduce((a, b) => a + b, 0)
  const daysRunning = Math.max(results.duration?.days_running ?? 1, 1)
  const experimentMonthlySessions = (expSessions / daysRunning) * 30
  if (experimentMonthlySessions <= 0) return null

  return {
    projectedMonthlyGain: lift.absolute * experimentMonthlySessions * treatmentAOV,
    liftAbsolute: lift.absolute,
    bestVariant,
    treatmentAOV,
    experimentMonthlySessions,
    usedStoreAOVFallback,
  }
}

/**
 * Project monthly impact for a completed experiment from its frozen
 * results_summary. Mirrors the active-experiment math but uses the recorded
 * winner instead of recomputing the best variant.
 */
export function projectCompletedExperimentMonthlyGain(
  experiment: Experiment,
  storeAOV: number = 0
): ExperimentImpactProjection | null {
  const summary = experiment.results_summary
  if (!summary?.is_significant || !summary.winner) return null

  const winnerKey = summary.winner
  const liftAbsolute = summary.lift?.absolute ?? 0
  if (liftAbsolute <= 0) return null

  const variant = summary.variants?.[winnerKey]
  let treatmentAOV = variant && variant.conversions > 0 ? variant.revenue / variant.conversions : 0
  let usedStoreAOVFallback = false
  if (treatmentAOV <= 0) {
    if (storeAOV > 0) {
      treatmentAOV = storeAOV
      usedStoreAOVFallback = true
    } else {
      return null
    }
  }

  const expSessions = summary.total_sessions ?? 0
  const startStr = (experiment as any).timing?.start_date_time || experiment.created_at
  const endStr = experiment.finalized_at || experiment.updated_at
  let daysRunning = 1
  if (startStr && endStr) {
    daysRunning = Math.max(
      1,
      Math.floor((new Date(endStr).getTime() - new Date(startStr).getTime()) / (1000 * 60 * 60 * 24))
    )
  }
  const experimentMonthlySessions = (expSessions / daysRunning) * 30
  if (experimentMonthlySessions <= 0) return null

  return {
    projectedMonthlyGain: liftAbsolute * experimentMonthlySessions * treatmentAOV,
    liftAbsolute,
    bestVariant: winnerKey,
    treatmentAOV,
    experimentMonthlySessions,
    usedStoreAOVFallback,
  }
}


// --- Attention Queue ---

export type AttentionType = 'srm_warning' | 'winner_ready' | 'low_traffic' | 'underperformer' | 'stale'

export interface AttentionItem {
  type: AttentionType
  priority: number
  experiment: Experiment
  results?: any
  // Key metrics for display
  liftPercentage?: number
  confidence?: number
  samplePercentage?: number
  daysSince?: number
  daysRunning?: number
  srmWarning?: string
}

// For negative experiments the hypothesis is control and baseline is treatment,
// so the relative lift must be recomputed against the treatment rate (the
// denominator changes — sign-flipping the raw percentage is wrong).
function hypothesisLiftFromRates(
  controlRate: number | undefined,
  treatmentRate: number | undefined,
  rawLiftPct: number | undefined,
  isNegative: boolean
): number | undefined {
  if (!isNegative) return rawLiftPct
  const c = controlRate ?? 0
  const t = treatmentRate ?? 0
  if (t > 0) return ((c - t) / t) * 100
  return rawLiftPct != null ? -rawLiftPct : undefined
}

export function buildAttentionQueue(
  experiments: Experiment[],
  resultsMap: Map<string, any>
): AttentionItem[] {
  const items: AttentionItem[] = []
  const now = new Date()

  for (const exp of experiments) {
    const results = resultsMap.get(exp.id)
    const isNegative = exp.is_negative === true

    if (exp.status === 'active' && results) {
      const summary = results.summary
      const duration = results.duration
      const ctrlRate = results.conversion_rate?.control
      const trtRate = results.conversion_rate?.treatment

      // SRM Warning
      if (results.srm_analysis && !results.srm_analysis.is_valid) {
        items.push({
          type: 'srm_warning',
          priority: 0,
          experiment: exp,
          results,
          srmWarning: results.srm_analysis.warning,
          daysRunning: duration?.days_running,
        })
        continue // SRM is the most critical — don't add other alerts for same experiment
      }

      // Winner Ready
      if (summary?.is_significant && summary?.winner) {
        const isReady = summary.auto_winner_requirements?.winner_ready
        if (isReady) {
          items.push({
            type: 'winner_ready',
            priority: 1,
            experiment: exp,
            results,
            liftPercentage: hypothesisLiftFromRates(ctrlRate, trtRate, summary.lift?.percentage, isNegative),
            confidence: summary.confidence_level,
            daysRunning: duration?.days_running,
          })
          continue
        }
      }

      const daysRunning = duration?.days_running ?? 0

      // Low Traffic
      if (daysRunning >= 7 && summary?.current_sample_percentage < 30) {
        items.push({
          type: 'low_traffic',
          priority: 2,
          experiment: exp,
          results,
          samplePercentage: summary.current_sample_percentage,
          daysRunning,
        })
        continue
      }

      // Underperformer — evaluate from the hypothesis perspective so negative
      // experiments only get flagged when the hypothesis (control) is losing.
      const hypLift = hypothesisLiftFromRates(ctrlRate, trtRate, summary?.lift?.percentage, isNegative)
      const hypIsNegative = isNegative
        ? (hypLift != null && hypLift < 0)
        : results.lift < 0
      if (daysRunning >= 14 && (hypIsNegative || results.confidence < 0.5)) {
        items.push({
          type: 'underperformer',
          priority: 3,
          experiment: exp,
          results,
          liftPercentage: hypLift,
          confidence: results.confidence,
          daysRunning,
        })
        continue
      }
    }

    // Completed with winner (not yet acted on)
    if (exp.status === 'completed' && exp.results_summary?.is_significant && exp.results_summary?.winner) {
      const variants = exp.results_summary.variants || {}
      const ctrlRate = variants.control?.conversion_rate
      const trtRate = variants.treatment?.conversion_rate
      items.push({
        type: 'winner_ready',
        priority: 1,
        experiment: exp,
        liftPercentage: hypothesisLiftFromRates(ctrlRate, trtRate, exp.results_summary.lift?.percentage, isNegative),
        confidence: undefined, // not stored in results_summary
        daysRunning: undefined,
      })
    }

    // Stale paused experiments
    if (exp.status === 'paused') {
      const daysSince = daysBetween(exp.updated_at, now)
      if (daysSince >= 7) {
        items.push({
          type: 'stale',
          priority: 4,
          experiment: exp,
          daysSince,
        })
      }
    }
  }

  // Sort by priority, then by daysRunning descending
  items.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority
    return (b.daysRunning ?? b.daysSince ?? 0) - (a.daysRunning ?? a.daysSince ?? 0)
  })

  return items
}

// --- Trend computation ---

export function computeTrend(timeSeries: any[] | undefined): 'up' | 'down' | 'flat' {
  if (!timeSeries || timeSeries.length < 3) return 'flat'

  const recent = timeSeries.slice(-3)
  const gap = (d: any) => (d.treatment_rate ?? 0) - (d.control_rate ?? 0)

  const first = gap(recent[0])
  const avg = (gap(recent[1]) + gap(recent[2])) / 2

  const diff = avg - first
  const threshold = 0.005 // 0.5pp change in the lift gap

  if (diff > threshold) return 'up'
  if (diff < -threshold) return 'down'
  return 'flat'
}
